/**
 * teams.ts — team-membership query helpers (v4M3.1).
 *
 * Implements the equivalence-relation reads over `Empire.teamId`:
 *
 *   - `areTeammates(state, a, b)` — true iff both empires carry the
 *     same non-null `teamId`. Solo (`null`) empires are NEVER
 *     teammates with anyone, including themselves under this query
 *     (callers that want "same empire OR teammate" should add the
 *     equality check explicitly).
 *
 *   - `getTeammates(state, empireId)` — the set of empire ids on the
 *     same team as `empireId`, excluding `empireId` itself. Empty
 *     for solo / null-team empires.
 *
 *   - `getTeamMembers(state, empireId)` — same set but INCLUDING
 *     `empireId` itself. For solo empires this returns just
 *     `[empireId]` — useful for visibility unions where the viewer
 *     is always in the union.
 *
 *   - `getTeamId(state, empireId)` — convenience accessor returning
 *     `null` for unknown empires (vs throwing). Cheap probe.
 *
 *   - `getTeamRoster(state)` — Map from `teamId` to member empire
 *     ids. Solo empires are omitted. Used by the win-condition pass
 *     and the alert-feed team-eliminated detector.
 *
 * Authoritative spec: `docs/TEAMS.md`. The data model is a single
 * field (`Empire.teamId: number | null`); no `state.teams` map
 * exists. Membership is fully expressible as an equivalence
 * relation over the empire set so these queries are stateless
 * derivations.
 */

import type { EmpireId, GameState } from '../state/types/index';

/**
 * v4M3.2 — true iff both empires share a non-null teamId. Solo
 * empires never count as teammates (a `null` teamId on either side
 * returns false). Order-independent: `areTeammates(a, b)` ===
 * `areTeammates(b, a)`. Same-empire-id is NOT auto-true — that's a
 * caller-side decision.
 */
export function areTeammates(
  state: GameState,
  a: EmpireId,
  b: EmpireId,
): boolean {
  const ea = state.empires.get(a);
  const eb = state.empires.get(b);
  if (!ea || !eb) return false;
  if (ea.teamId === null || eb.teamId === null) return false;
  return ea.teamId === eb.teamId;
}

/**
 * v4M3.2 — list of empire ids on the same team as `empireId`,
 * EXCLUDING `empireId` itself. Empty for solo empires. Order is
 * insertion-stable per `state.empires.entries()` so callers can
 * compare arrays directly in tests.
 */
export function getTeammates(state: GameState, empireId: EmpireId): EmpireId[] {
  const self = state.empires.get(empireId);
  if (!self || self.teamId === null) return [];
  const out: EmpireId[] = [];
  for (const [id, e] of state.empires) {
    if (id === empireId) continue;
    if (e.teamId === self.teamId) out.push(id);
  }
  return out;
}

/**
 * v4M3.2 — the empire AND all of its teammates, as a stable-ordered
 * list. For solo empires this is just `[empireId]`. The visibility
 * union pattern in `advance-detection.ts` reads from this directly:
 * the viewer + every teammate's per-empire vision contribution
 * unions across this set.
 */
export function getTeamMembers(
  state: GameState,
  empireId: EmpireId,
): EmpireId[] {
  return [empireId, ...getTeammates(state, empireId)];
}

/**
 * v4M3.4 — small accessor for the empire's teamId. Returns `null`
 * for missing empires (rather than throwing) since the win-condition
 * pass iterates the empires map and a probe is cheaper than a
 * try/get pair.
 */
export function getTeamId(
  state: GameState,
  empireId: EmpireId,
): number | null {
  return state.empires.get(empireId)?.teamId ?? null;
}

/**
 * v4M3.4 — team-id → empire-id list map. Solo empires (teamId === null)
 * are omitted from the result; iterate `state.empires` directly when
 * solo empires need to be considered. The map is built fresh each
 * call — no caching — so it stays correct after deserialise +
 * mid-session state mutations (none today, but defensive against
 * v5+ "team reassign" mechanics if those ever land).
 */
export function getTeamRoster(state: GameState): Map<number, EmpireId[]> {
  const out = new Map<number, EmpireId[]>();
  for (const [id, e] of state.empires) {
    if (e.teamId === null) continue;
    const existing = out.get(e.teamId);
    if (existing) {
      existing.push(id);
    } else {
      out.set(e.teamId, [id]);
    }
  }
  return out;
}
